<template>
  <div class="flex max-w-256 lg:mx-auto">
    <button class="hidden m:flex m:pr-4" :disabled="!leftNavIconActive" @click="slideImage(-1)">
      <sprite id="app/arrow" :class="`transform rotate-90 flex-shrink-0 mt-20 ${leftNavIconClasses}`" />
    </button>

    <div class="w-full overflow-hidden">
      <div
        class="flex gap-12 overflow-x-auto overflow-y-hidden -mb-16 pb-16 px-4 snap-x scroll-pl-4 scroll-pr-4"
        ref="bookCards"
      >
        <div class="flex-shrink-0 w-44 snap-start" ref="bookCardWrappers" :key="book.id" v-for="book in books">
          <book-card-mini :book="book" :show-description="true" :is-book-linked="areBooksLinked" />
        </div>
        <cta-card
          data-is-last
          class="snap-start"
          @click="goToOnboarding"
          event-category="book-slider"
          :signup-headline="$t('signupHeadline')"
          v-if="showCtaCard"
          v-html="getAccessToBooks"
        />
      </div>
    </div>

    <button class="hidden m:flex m:pl-4" :disabled="!rightNavIconActive" @click="slideImage(1)">
      <sprite id="app/arrow" :class="`transform -rotate-90 flex-shrink-0 mt-20 ${rightNavIconClasses}`" />
    </button>
  </div>
</template>

<script>
import BookCardMini from "@/shared/book-card/book-card-mini"
import CtaCard from "@/component-library/cta-card"
import Sprite from "@/shared/sprite"
import smoothscroll from "smoothscroll-polyfill"

smoothscroll.polyfill()

export default {
  name: "BookSliderWithArrows",
  components: {
    BookCardMini,
    Sprite,
    CtaCard
  },
  props: {
    books: {
      type: Array,
      default: () => [],
      required: true
    },
    booksCount: {
      type: Number,
      default: 0
    },
    categoryTitle: {
      type: String,
      default: ""
    },
    areBooksLinked: {
      type: Boolean,
      default: true
    },
    showCtaCard: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      scrollingPosX: 0,
      containerWidth: window.innerWidth,
      bookCardWidth: 0,
      bookCardMarginRight: 0
    }
  },
  computed: {
    getAccessToBooks() {
      return this.$t("getAccessToBooks", {
        category: this.categoryTitle,
        totalBooks: this.booksCount
      })
    },
    leftNavIconClasses() {
      return this.leftNavIconActive ? "text-blue cursor-pointer" : "text-light-grey cursor-not-allowed"
    },
    rightNavIconClasses() {
      return this.rightNavIconActive ? "text-blue cursor-pointer" : "text-light-grey cursor-not-allowed"
    },
    rightNavIconActive() {
      return this.scrollingPosX < this.maximumScrollingPositionX
    },
    leftNavIconActive() {
      return this.scrollingPosX > 0
    },
    baseWidth() {
      return this.bookCardWidth + this.bookCardMarginRight
    },
    maximumScrollingPositionX() {
      return this.baseWidth * this.cardsCount + this.bookCardWidth - this.containerWidth
    },
    cardsCount() {
      return this.showCtaCard ? this.books.length : this.books.length - 1
    }
  },
  mounted() {
    if (this.$refs && this.$refs.bookCards) {
      this.containerWidth = this.$refs.bookCards.clientWidth
      this.$refs.bookCards.addEventListener("scroll", this.updateScrollPosition, { passive: true })
    }

    if (this.$refs && this.$refs.bookCardWrappers && this.$refs.bookCardWrappers.length > 0) {
      const firstBookCardWrapper = this.$refs.bookCardWrappers[0]
      this.setBookCardWitdth(firstBookCardWrapper)
      this.setBookCardMarginRight(firstBookCardWrapper)
    }
  },
  beforeDestroy() {
    this.$refs.bookCards.removeEventListener("scroll", this.updateScrollPosition, { passive: true })
  },
  methods: {
    setBookCardWitdth(firstBookCardWrapper) {
      this.bookCardWidth = firstBookCardWrapper.clientWidth
    },
    setBookCardMarginRight(firstBookCardWrapper) {
      const bookCardComputedStyle = window.getComputedStyle(firstBookCardWrapper)
      const bookCardMarginRightPX = bookCardComputedStyle.getPropertyValue("margin-right")

      this.bookCardMarginRight = parseFloat(bookCardMarginRightPX.replace("px", ""))
    },
    updateScrollPosition() {
      this.scrollingPosX = this.$refs.bookCards.scrollLeft
    },
    slideImage: async function (direction) {
      const distanceToBeScrolled = Math.floor(this.containerWidth / this.baseWidth) * this.baseWidth

      this.scrollingPosX += direction * distanceToBeScrolled

      if (this.scrollingPosX < 0) {
        this.scrollingPosX = 0
      } else if (this.scrollingPosX >= this.maximumScrollingPositionX) {
        this.scrollingPosX = this.maximumScrollingPositionX
      }

      this.$refs.bookCards.scroll({
        behavior: "smooth",
        left: this.scrollingPosX,
        top: 0
      })
    },
    goToOnboarding() {
      window.location.href = `/${this.$i18n.locale}/onboarding/matrix`
    }
  },
  i18n: {
    messages: {
      en: {
        getAccessToBooks: "<strong><mark>Get access to all {totalBooks} titles</strong></mark> in {category}",
        signupHeadline: "Sign up for Blinkist"
      },
      de: {
        getAccessToBooks: "<strong><mark>Entdecke alle {totalBooks} Titel</mark></strong>  in {category}",
        signupHeadline: "Bei Blinkist registrieren"
      },
      es: {
        getAccessToBooks: "<strong><mark>Get access to all {totalBooks} titles</strong></mark> in {category}",
        signupHeadline: "Sign up for Blinkist"
      }
    }
  }
}
</script>

<style media="screen">
.snap-x {
  scroll-snap-type: x mandatory;
}

.scroll-pl-4 {
  scroll-padding-left: 1rem;
}

.scroll-pr-4 {
  scroll-padding-right: 1rem;
}

.snap-start {
  scroll-snap-align: start;
}
</style>
